import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CustomEditFieldComponent, Form, FormMode } from '../../../../../../base/form';
import { BehaviorSubject, defer, EMPTY, Observable, Subscription } from 'rxjs';
import {
  arr_filter,
  arr_map,
  blank,
  data_get,
  fallback,
  filled,
  generateId,
  map_to_arr,
  observe,
  OptionalValue,
  spf,
  transform
} from '../../../../../../shared/utils/common';
import { FormGroup } from '@angular/forms';
import { ApiService } from '../../../../../../services/api.service';
import { FormService, StudioFormTab } from '../../../../../../services/form.service';
import { NotificationService } from '../../../../../../services/notification.service';
import { DIALOG_DATA, DialogService } from '../../../../../../services/dialog.service';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { AssetTypeFaultSeverity } from '../../../types';
import { FieldmagicDropdownOption } from '../../../../../../shared/components/forms/input/dropdown/dropdown.component';
import { TranslateService } from '@ngx-translate/core';
import { RelateOption } from '../../../../../../shared/components/forms/input/relate/relate-input.component';

@Component({
  selector: 'asset-types-fault-codes-input',
  template: `
    <div class="d-flex d-flex-gap-2x flex-column">
      <div fieldmagicGridRow>
        <div
          fieldmagicGridColumn="12"
          class="d-flex d-flex-gap justify-content-end"
        >
         <fieldmagic-primary-button
            label="Add Severity"
            [icon]="[
              {
                icon: 'plus-circle',
                klass: 'text-success'
              }
            ]"
            purpose="icon"
            (click)="onAddSeverity()"
          >
          </fieldmagic-primary-button>

          <fieldmagic-primary-button
            purpose="ai"
            [isInProgress]="generating$ | async"
            (click)="onGenerate()"
          >
          </fieldmagic-primary-button>
        </div>
      </div>

      <div fieldmagicGridRow>
        <div fieldmagicGridColumn="12">
          <table class="table table-bordered">
            <thead>
              <tr>
                <td width="30%">
                  <fieldmagic-text
                    content="Defects"
                    purpose="table-header"
                    [withRequiredMarker]="true"
                  >
                  </fieldmagic-text>
                </td>
                <td width="50%">
                  <fieldmagic-text
                    content="Description"
                    purpose="table-header"
                  >
                  </fieldmagic-text>
                </td>
                <td width="20%">
                  <fieldmagic-text
                    content="Linked Product"
                    purpose="table-header"
                  >
                  </fieldmagic-text>
                </td>
                <td width="5%">
                </td>
              <tr>
            </thead>

            <tbody>
              <ng-container *ngIf="fetching$ | async">
                <tr class="text-center">
                  <td colspan="4">
                    <fieldmagic-icon [spin]="true"></fieldmagic-icon>
                  </td>
                </tr>
              </ng-container>

              <ng-container *ngIf="! (fetching$ | async)">
                <ng-container *ngIf="severities$ | async as severities">
                  <tr
                    *ngIf="isEmpty$ | async"
                    class="text-center"
                  >
                    <td colspan="4">No defects available.</td>
                  </tr>

                  <ng-container *ngFor="let severity of severities; let severity_index = index;">
                    <ng-container *ngIf="! severity.deleted">
                      <tr >
                        <td colspan="4">
                          <div class="d-flex d-flex-gap w-100 align-items-center">
                            <fieldmagic-color-picker-input
                              [standalone]="true"
                              [(ngModel)]="severity.color"
                              [displayValue]="false"
                              [displayOnly]="true"
                            >
                            </fieldmagic-color-picker-input>

                            <fieldmagic-dropdown-input
                              class="w-30"
                              bindValue="dropdown_key"
                              [(ngModel)]="severity.dropdown_key"
                              [standalone]="true"
                              [options]="options$ | async"
                              placeholder="Please select a severity"
                              (ngModelChange)="onSeverityChange($event, severity_index)"
                            >
                            </fieldmagic-dropdown-input>

                            <fieldmagic-primary-button
                              class="ml-auto"
                              tooltip="Remove Severity"
                              purpose="icon"
                              [icon]="[
                                {
                                  icon: 'minus-circle',
                                  klass: 'text-danger'
                                }
                              ]"
                              (click)="onSeverityRemove(severity_index)"
                            >
                            </fieldmagic-primary-button>
                          </div>
                        </td>
                      </tr>
                      <ng-container *ngFor="let fault of severity.faults; let fault_index = index;">
                        <tr *ngIf="! fault.deleted">
                          <td>
                            <fieldmagic-textarea-input
                              class="w-100"
                              placeholder="Defect"
                              [expandable]="true"
                              [standalone]="true"
                              [(ngModel)]="fault.fault"
                              (ngModelChange)="emitChanges()"
                            >
                            </fieldmagic-textarea-input>
                          </td>

                          <td>
                            <fieldmagic-textarea-input
                              class="w-100"
                              placeholder="Description"
                              [expandable]="true"
                              [standalone]="true"
                              [(ngModel)]="fault.description"
                              (ngModelChange)="emitChanges()"
                            >
                            </fieldmagic-textarea-input>
                          </td>

                          <td>
                            <fieldmagic-relate-input
                              [(ngModel)]="fault.item_id"
                              placeholder="Please select a product linked to this defect.."
                              [standalone]="true"
                              moduleName="items"
                              [filter]="{
                                'active': true,
                                'labor': false
                              }"
                              (ngModelChange)="emitChanges()"
                            >
                            </fieldmagic-relate-input>
                          </td>

                          <td>
                            <fieldmagic-primary-button
                              tooltip="Add Fault"
                              purpose="icon"
                              [icon]="[{
                                icon: 'plus-circle',
                                klass: 'text-success'
                              }]"
                              (click)="onAddFault(severity_index)"
                            >
                            </fieldmagic-primary-button>

                            <fieldmagic-primary-button
                              purpose="delete-icon"
                              (click)="onFaultRemove(severity_index, fault_index)"
                              [disabled]="! isSeverityFaultRemovable(severity)"
                              tooltip="Remove Fault"
                            >
                            </fieldmagic-primary-button>
                          </td>
                        </tr>
                      </ng-container>
                    </ng-container>
                  </ng-container>
                </ng-container>
              </ng-container>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `
})
export class AssetTypeFaultCodesInputComponent implements OnInit, OnDestroy, CustomEditFieldComponent {
  readonly fetching$ = new BehaviorSubject<boolean>(true);
  readonly generating$ = new BehaviorSubject<boolean>(false);

  readonly severities$ = new BehaviorSubject<AssetTypeFaultSeverityInputData[]>([]);
  readonly options$ = new BehaviorSubject<AssetTypeSeverityOption[]>([]);
  readonly isEmpty$ = new BehaviorSubject<boolean>(false);

  private readonly _mode$ = new BehaviorSubject<FormMode>('add');
  private readonly _field$ = new BehaviorSubject<OptionalValue<Form<any>>>(null);
  private readonly _parent$ = new BehaviorSubject<OptionalValue<FormGroup>>(null);
  private readonly _data$ = new BehaviorSubject<AssetTypeFaultCodesInputData>({});
  private readonly _name$ = new BehaviorSubject<OptionalValue<string>>(null);
  private readonly _applicableStandard$ = new BehaviorSubject<OptionalValue<string>>(null);

  private _onFetchingFaultCodesSubscription?: Subscription;
  private _onNameFieldChangeSubscription?: Subscription;
  private _onApplicationStandardsFieldChangeSubscription?: Subscription;
  private _onFetchingSeverityOptionsSubscription?: Subscription;
  private _onSeveritiesChangeSubscription?: Subscription;

  constructor(
    private readonly _api: ApiService,
    private readonly _forms: FormService,
    private readonly _notifications: NotificationService,
    private readonly _dialog: DialogService,
    private readonly _translator: TranslateService,
  ) { }

  ngOnInit(): void {
    this._onFetchingSeverityOptionsSubscription = this._api.callPrivate$({
      action: 'post',
      path: '/dropdown/field_options',
      body: {
        field: 'severity',
        module: 'asset_type_severities',
      },
      onSuccessResponse: (response) => map_to_arr(response),
    }).subscribe((options) => this.options$.next(arr_map(options, {
      transformer: (option) => ({
        text: this._translator.instant(data_get(option, 'name')),
        dropdown_key: data_get(option, 'key'),
        color: data_get(option, 'color'),
        position: data_get(option, 'position'),
      }),
    })));

    if (this._mode$.value == 'edit') {
      this._onFetchingFaultCodesSubscription = observe({
        after: () => this.fetching$.next(false),
        observable: () => this._api.callPrivate$({
          path: '/asset_types/fault_codes',
          action: 'post',
          expectsNewFormat: true,
          body: {
            'asset_type_id': this._data$.value.record_id,
          },
          onSuccessResponse: (response) => arr_map(response.get('items'), {
            transformer: (value) => ({
              ...value,
              faults: arr_map(data_get(value, 'faults'), {
                transformer: (fault) => ({
                  ... fault,
                  item_id: transform(data_get(fault, 'item_text'), {
                    transformer: (text) => ({
                      id: data_get(fault, 'item_id'),
                      text,
                    }),
                  }),
                }),
              }),
            }),
          }),
        })
      })
        .pipe(
          filter(filled),
        )
        .subscribe((severities) => this.severities$.next(severities));
    } else {
      this.fetching$.next(false);
    }

    this._listenToFieldChanges();

   this._onSeveritiesChangeSubscription = this.severities$.pipe(
    tap((severities) => {
      const available = arr_filter(severities, {
        predicate: (severity) => ! severity.deleted,
      });

      this.isEmpty$.next(blank(available));
    }),
   ).subscribe((severities) => this._parent$.value.setValue({
      fault_codes: arr_map(severities, {
        transformer: (severity) => ({
          ... severity,
          faults: arr_map(severity.faults, {
            transformer: (fault) => ({
              ... fault,
              item_id: data_get(fault.item_id, 'id'),
            }),
          }),
        }),
      }),
    }));
  }

  ngOnDestroy(): void {
    if (this._onFetchingFaultCodesSubscription) {
      this._onFetchingFaultCodesSubscription.unsubscribe();
    }

    if (this._onNameFieldChangeSubscription) {
      this._onNameFieldChangeSubscription.unsubscribe();
    }

    if (this._onApplicationStandardsFieldChangeSubscription) {
      this._onApplicationStandardsFieldChangeSubscription.unsubscribe();
    }

    if (this._onFetchingSeverityOptionsSubscription) {
      this._onFetchingSeverityOptionsSubscription.unsubscribe();
    }

    if (this._onSeveritiesChangeSubscription) {
      this._onSeveritiesChangeSubscription.unsubscribe();
    }
  }

  /**
   * @inheritdoc
   */
  setFormMode = (mode: FormMode) => this._mode$.next(mode);

  /**
   * @inheritdoc
   */
  setParentForm = (parent: FormGroup) => this._parent$.next(parent);

  /**
   * @inheritdoc
   */
  setField = (field: Form<any>) => this._field$.next(field);

  /**
   * @inheritdoc
   */
  setAdditionalData = (data: AssetTypeFaultCodesInputData) => this._data$.next(data);

  isSeverityFaultRemovable = (severity: AssetTypeFaultSeverityInputData) => arr_filter(severity.faults, {
    predicate: (fault) => !fault.deleted
  }).length > 1;

  onSeverityChange(selected: OptionalValue<AssetTypeSeverityOption>, index: number): void {
    if (index < 0) {
      return;
    }

    const severities = [... this.severities$.value];
    let severity = severities[index];

    if (blank(selected)) {
      severity = {
        ... severity,
        severity: null,
        color: '#868e96',
        dropdown_key: null,
        severity_level: -1,
      };
    } else {
      severity = {
        ... severity,
        color: data_get(selected, 'color'),
        severity: data_get(selected, 'text'),
        dropdown_key: data_get(selected, 'dropdown_key'),
        severity_level: data_get(selected, 'position'),
      }
    }

    severities[index] = severity;

    this.severities$.next([... severities]);
  }

  onAddSeverity = () => this.severities$.next([
    ... this.severities$.value,
    ...[{
      id: generateId(),
      severity: null,
      color: '#868e96',
      faults: [{
        id: generateId(),
        fault: '',
        description: '',
        item_id: null,
      }],
      dropdown_key: null,
      severity_level: -1,
    }],
  ]);

  onAddFault(index: number): void {
    const current = this.severities$.value;

    if (current.length < index || index < 0) {
      return;
    }

    const severity = current[index];

    severity.faults.push({
      id: generateId(),
      fault: '',
      description: '',
    });

    current[index] = severity;

    this.severities$.next(current);
  }

  onSeverityRemove(index: number): void {
    const current = this.severities$.value;

    if (current.length < index || index < 0) {
      return;
    }

    const severity: AssetTypeFaultSeverityInputData = {
      ...current[index],
      deleted: true,
    };

    current[index] = severity;

    this.severities$.next([...current]);
  }

  onFaultRemove(parent: number, index: number): void {
    const current = this.severities$.value;

    if (current.length < parent || parent < 0) {
      return;
    }

    const severity = current[parent];
    const faults = arr_filter(severity.faults, {
      predicate: (fault) => !fault.deleted,
    });

    if (faults.length < 2 || index < 0) {
      return;
    }

    const fault: AssetTypeFaultInputData = {
      ...severity.faults[index],
      deleted: true,
    };

    severity.faults[index] = fault;
    current[parent] = severity;

    this.severities$.next([...current]);
  }

  onGenerate = () => observe({
    before: () => this.generating$.next(true),
    after: () => this.generating$.next(false),
    observable: () => defer(() => {
      if (blank(this._name$.value)) {
        this._notifications.notifyError('Please set the name of the asset type in order to generate possible defects');
        return EMPTY;
      }

      return new Observable<AssetTypeFaultCodesSpecificsDialogResult>((observer) => {
        this._dialog.show<AssetTypeFaultCodesSpecificsDialogComponent, AssetTypeFaultCodesSpecificsDialogData>({
          component: AssetTypeFaultCodesSpecificsDialogComponent,
          data: {
            name: this._name$.value,
            applicable_standards: this._applicableStandard$.value,
          },
          afterClosing: (results) => observer.next(results),
        });
      });
    })
  })
    .pipe(
      filter(filled),
      tap(() => this._notifications.notifySuccess('Possible defects generated successfully.')),
    )
    .subscribe((severities) => this.severities$.next([
      ...arr_map(this.severities$.value, {
        transformer: (severity) => ({
          ...severity,
          deleted: true,
        })
      }),
      ...severities,
    ]));

  emitChanges = () => this.severities$.next([... this.severities$.value]);

  private _listenToFieldChanges(): void {
    const tabs: StudioFormTab[] = fallback(this._forms.tabs, {
      fallback: () => [],
    });

    for (const tab of tabs) {
      for (const field of tab.fields) {
        if (field.key == 'name') {
          /// set the current
          this._name$.next(field.default_value);

          this._onNameFieldChangeSubscription = tab.groups.controls['name'].valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
          ).subscribe((value) => this._name$.next(value));
        } else if (field.key == 'applicable_standards') {
          /// set the current
          this._applicableStandard$.next(field.default_value);

          this._onApplicationStandardsFieldChangeSubscription = tab.groups.controls['applicable_standards'].valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
          ).subscribe((value) => this._applicableStandard$.next(value));
        }
      }
    }
  }
}

type AssetTypeFaultCodesInputData = {
  record_id?: string;
}

type AssetTypeFaultSeverityInputData = {
  id: string;
  severity: string;
  dropdown_key: OptionalValue<string>;
  color: string;
  severity_level: number;
  faults: AssetTypeFaultInputData[];
  deleted?: boolean;
}

type AssetTypeFaultInputData = {
  id: string;
  fault: string;
  deleted?: boolean;
  description?: string;
  item_id?: RelateOption;
}

@Component({
  selector: 'asset-types-fault-codes-specifics-dialog',
  template: `
    <fieldmagic-dialog
      label="Generate with AI"
      icon="magic"
    >
      <ng-template fieldmagicDialogButtonsTmp>
        <fieldmagic-primary-button
          [isInProgress]="processing$ | async"
          label="Generate"
          icon="magic"
          (click)="onGenerate()"
        >
        </fieldmagic-primary-button>

        <fieldmagic-dialog-close-button
          [instance]="self"
          [disabled]="processing$ | async"
        >
        </fieldmagic-dialog-close-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <fieldmagic-textarea-input
          [standalone]="false"
          label="Tell me more about the possible faults to be generated (This is optional)."
          [(ngModel)]="specifics"
          placeholder="Like standards, etc.."
        >
        </fieldmagic-textarea-input>
      </ng-template>
    </fieldmagic-dialog>
  `,
})
export class AssetTypeFaultCodesSpecificsDialogComponent implements OnInit {
  specifics?: string;

  readonly processing$ = new BehaviorSubject<boolean>(false);

  get self() {
    return this;
  }

  constructor(
    @Inject(DIALOG_DATA) private readonly _data: AssetTypeFaultCodesSpecificsDialogData,
    private readonly _notifications: NotificationService,
    private readonly _api: ApiService,
    private readonly _dialog: DialogService,
    private readonly _translator: TranslateService,
  ) { }

  ngOnInit(): void {
    if (filled(this._data.applicable_standards)) {
      this.specifics = spf('Use %s standard', {
        args: [this._data.applicable_standards],
      });
    }
  }

  onGenerate = () => observe({
    before: () => this.processing$.next(true),
    after: () => this.processing$.next(false),
    observable: () => this._api.callPrivate$({
      path: '/asset_types/fault_codes/generate',
      action: 'post',
      headers: {
        'X-Captcha': 'generate_asset_type_fault_codes',
      },
      body: {
        'asset_type_name': this._data.name,
        'specifics': this.specifics,
      },
      expectsNewFormat: true,
      onSuccessResponse: (response) => {
        const results = response.get('items');
        const severities: AssetTypeFaultSeverityInputData[] = [];

        for (const result of results) {
          const severity: AssetTypeFaultSeverityInputData = {
            id: data_get(result, 'id'),
            severity: this._translator.instant(data_get(result, 'severity')),
            color: fallback(data_get(result, 'color'), {
              fallback: () => '#868e96',
            }).toLowerCase(),
            faults: [],
            dropdown_key: data_get(result, 'dropdown_key'),
            severity_level: fallback(data_get(result, 'severity_level'), {
              fallback: () => 0,
            }),
          };

          const children = fallback(data_get(result, 'faults'), {
            fallback: () => [],
          });

          for (const child of children) {
            severity.faults.push({
              id: data_get(child, 'id'),
              fault: data_get(child, 'fault'),
              description: data_get(child, 'description'),
              item_id: null,
            });
          }

          if (blank(severity.faults)) {
            continue;
          }

          severities.push(severity);
        }

        return severities;
      },
    }),
  }).subscribe((severities) => {
    if (blank(severities)) {
      return this._notifications.notifyError('No possible defects generated. Please try again with different specifics.');
    }

    return this._dialog.close({
      instance: this,
      result: severities,
    });
  });
}

type AssetTypeFaultCodesSpecificsDialogData = {
  name: string;
  applicable_standards?: string;
}

type AssetTypeFaultCodesSpecificsDialogResult = AssetTypeFaultSeverity[];

type AssetTypeSeverityOption = FieldmagicDropdownOption<{
  dropdown_key: string;
  color: string;
  position: number;
}>